import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactModal from 'react-modal';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { generateGeneralProject } from 'src/admin/apiUsers';
import { Button } from 'src/common/components/buttons/Button';
import { trackProjectCreationAnalytics } from 'src/project/components/CreateProject/utils';

type Props = {
  user_id: string;
  projectId: string;
  projectName: string;
  shouldCloneFully: string;
  size?: 'sm' | 'md';
  className?: string;
};

export const CopyProjectButton = ({
  user_id,
  projectId,
  projectName,
  shouldCloneFully,
  size = 'md',
  className,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmCopyModal, setShowConfirmCopyModal] = useState(false);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCloseCopyModal = () => {
    setShowConfirmCopyModal(false);
  };

  const handleButtonClick = () => {
    if (shouldCloneFully === 'true') {
      createGeneralProject();
    } else {
      setShowConfirmCopyModal(true);
    }
  };

  const createGeneralProject = async () => {
    setIsGenerating(true);
    setShowConfirmCopyModal(false);
    try {
      if (!user_id || !projectId) {
        throw new Error();
      }

      const result = await generateGeneralProject(
        user_id,
        projectId,
        shouldCloneFully,
      );

      if (result.status.toLowerCase() === 'ok') {
        toast.success(t('admin:users.general.generateGeneralProjectSuccess'));
        const newProjectId = result.newProjectId;
        const newProjectName = result.newProjectName;

        if (newProjectId && newProjectName) {
          trackProjectCreationAnalytics(newProjectId, newProjectName);
          navigate(`/p/${newProjectId}/dashboard/overview`);
        }
      } else if (result.message?.toLowerCase().includes('project')) {
        if (shouldCloneFully === 'true') {
          setShowModal(true);
        }
        toast.error(t('admin:users.general.generalProjectExists'));
        return;
      }
    } catch (exception) {
      toast.error(t('admin:users.general.generateGeneralProjectFailure'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button
        variant="primary"
        onClick={handleButtonClick}
        disabled={isGenerating}
        loading={isGenerating}
        size={size}
        className={className}
      >
        {shouldCloneFully === 'true'
          ? t('common:button.addDemoProject')
          : t('common:button.copyProject')}
      </Button>

      <ReactModal
        isOpen={showModal}
        onRequestClose={() => handleCloseModal()}
        contentLabel={t('admin:users.general.projectExistsErrorTitle')}
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <h2 className="mb-4 text-xl font-bold">
          {t('admin:users.general.projectExistsError')}
        </h2>
        <p className="mb-6">
          {shouldCloneFully === 'true'
            ? t('admin:users.general.projectCloneFullyAlreadyExistsMessage')
            : t('admin:users.general.projectAlreadyExistsMessage')}
        </p>
        <div className="flex justify-end gap-2">
          <Button onClick={() => handleCloseModal()} variant="primary">
            {t('common:button.confirm')}
          </Button>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={showConfirmCopyModal}
        onRequestClose={() => handleCloseCopyModal()}
        contentLabel={t('admin:users.general.confirmCopyTemplateProject')}
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <div className="p-4">
          <p className="mb-4 text-xl">
            {t('admin:users.general.copyTemplateProjectLicense')}
          </p>
          <p className="mb-6 text-xl">
            {t('admin:users.general.copyTemplateProjectName')}{' '}
            <b>{projectName}_Copy</b>
          </p>
          <div className="flex justify-end gap-4">
            <Button onClick={handleCloseCopyModal} variant="secondary">
              {t('common:button.abort')}
            </Button>
            <Button onClick={createGeneralProject} variant="primary">
              {t('common:button.confirm')}
            </Button>
          </div>
        </div>
      </ReactModal>
    </>
  );
};
